import { debounce } from '@ember/runloop';
import { cached } from '@glimmer/tracking';

import type Highcharts from 'highcharts';

import type { DChartInterface } from 'webapp-types';
import { scaleValToAutofit } from 'webapp/utils/html';
import { formatNumber } from 'webapp/utils/number';

import type VisualizationPie from 'webapp/models-custom/visualization/pie';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import BaseRenderer from 'webapp/d-chart/renderer/base';
import type DChartSeries from 'webapp/d-chart/series/series';

interface PiePoint extends Highcharts.Point {
	dbxPoint: DChartPrimaryPoint;
	prevSliceShape?: Highcharts.SVGElement | null;
}

// the pie series as built here; its type comes from the chart options
type PieSeriesOptions = Omit<Highcharts.SeriesPieOptions, 'type' | 'data'> & {
	data: DChartInterface.ChartDataPoint[];
};

// Highcharts internals read when placing the center elements
type PieSeriesInternals = Highcharts.Series & {
	center: [number, number, number, number]; // x, y, size, innerSize
	points: (PiePoint & {
		shapeArgs: { x: number; y: number; r: number; innerR: number };
	})[];
};

// the HTML label's container, and its visibility attribute
type HtmlLabel = Highcharts.SVGElement & { div: HTMLElement; visibility?: string };

// a point in Highcharts' point events, which type it as a plain Point
type PieEventPoint = Highcharts.Point & Partial<Omit<PiePoint, keyof Highcharts.Point>>;

export default class PieRenderer extends BaseRenderer {
	aggregationLabel: Highcharts.SVGElement | null = null; // the one in the center
	shadowArc: Highcharts.SVGElement | null = null; // the inner shadow circle

	override get visualization(): VisualizationPie {
		return super.visualization as VisualizationPie;
	}

	get series(): DChartSeries | null {
		return this.visualization.series; // just one series
	}

	get hasLegend() {
		return this.visualization.isLegendBottom;
	}

	get showValues() {
		return this.visualization.showValues;
	}

	@cached
	get totalValueSum(): number {
		let sum = 0;
		this.series?.points.forEach((p) => {
			sum += p.value || 0;
		});
		return sum;
	}

	get isEmpty(): boolean {
		// empty is with less than 2 points and value 0
		// or with multiple points but all of them null or 0
		const series = this.series;
		return (
			!series ||
			(series.points.length <= 1 && !this.totalValueSum) ||
			!series.points.some((p) => {
				return !!p.value;
			})
		);
	}

	override get baseOptions(): Highcharts.Options {
		let options = super.baseOptions;

		const that = this;

		options = this.mergeOptions(options, {
			chart: {
				type: 'pie',
				events: {
					render: function (this: Highcharts.Chart, _event: Event) {
						that.updateDynamicElementsOnRender(this);
						if (that.hasLegend) {
							debounce(that, that.centerLegendItems, this.legend, 500);
						}
					},
				},
			},
			legend: {
				enabled: this.hasLegend,
			},
			plotOptions: {
				pie: {
					animation: this.dChart.isAnimated,
					dataLabels: {
						useHTML: true,
						connectorShape: 'straight',
					},
					point: {
						events: {
							mouseOver: function (this: PieEventPoint) {
								const hChart = this.series.chart,
									total = that.totalValueSum,
									compare = this.dbxPoint!.comparePoints[0];

								if (!total || !compare?.value) {
									return;
								}

								// the arc of a pie slice (Highcharts types shapeArgs as any SVG attributes)
								const shapeArgs = this.shapeArgs as
									| {
											x: number;
											y: number;
											r: number;
											innerR: number;
											start: number;
									  }
									| undefined;

								if (!shapeArgs) {
									return;
								}

								const sizeInPercent = Math.min(1, compare.value / total), // max is 1
									sizeInRadians = sizeInPercent * Math.PI * 2, // whole circle is (Math.PI * 2) radians
									sliceThickness = shapeArgs.r - shapeArgs.innerR,
									centerX = shapeArgs.x + hChart.plotLeft,
									centerY = shapeArgs.y + hChart.plotTop;

								const arc = hChart.renderer.arc(
									centerX,
									centerY,
									shapeArgs.r + Math.min(sliceThickness / 3, 20),
									shapeArgs.r + 1,
									shapeArgs.start,
									shapeArgs.start + sizeInRadians,
								);
								arc.addClass('compare-arc')
									.addClass(`highcharts-color-${this.colorIndex}`)
									.add();

								this.prevSliceShape = arc;
							},
							mouseOut: function (this: PieEventPoint) {
								if (this.prevSliceShape) {
									this.prevSliceShape.destroy();
									this.prevSliceShape = null;
								}
							},

							click: function (this: PieEventPoint, event: PointerEvent) {
								that.onPointClick(this.dbxPoint!, event, this.graphic?.element);
							},
						},
					},
				},
			},
		});

		return options;
	}

	override get dataOptions(): Highcharts.Options {
		const that = this;

		const options: Omit<Highcharts.Options, 'series'> & { series: PieSeriesOptions[] } = {
			series: [],
			legend: {
				enabled: this.hasLegend,
			},
		};

		const showValues = this.showValues,
			isOutsideLegend = this.visualization.isLegendOutside; // "spider legs"

		const seriesData: PieSeriesOptions = {
			innerSize: '65%',
			data: [],
			dataLabels: {
				enabled: showValues || isOutsideLegend,
				connectorShape: isOutsideLegend ? 'straight' : 'fixedOffset',
				distance: isOutsideLegend ? '20%' : '-17%',
				formatter: function (
					this: Highcharts.Point & { point?: DChartInterface.ChartHoverPoint },
				) {
					const valueStr = this.point!.dbxPoint.valueStr;
					if (isOutsideLegend) {
						let valueStrText = '',
							percentageStr = `${Math.round(this.point!.percentage! * 10) / 10}%`;
						if (showValues) {
							valueStrText = `<strong>${valueStr}</strong>`;
							percentageStr = ` (${percentageStr})`;
						}
						const text = `<span class="title">${that.sanitize(this.point!.name)}</span>${valueStrText}${percentageStr}`;
						return `<span class="label-outer">${text}</span>`;
					} else {
						return `<span class="label-inner">${valueStr}</span>`;
					}
				},
			},
			showInLegend: true,
		};

		let seriesDefaultClass = '';

		if (!this.isEmpty) {
			const series = this.series!;

			let hasNegative = false,
				isAllNegative = true;

			series.points.forEach((point) => {
				if (!point.value) {
					return;
				}
				if (point.value < 0) {
					hasNegative = true;
				} else if (point.value > 0) {
					isAllNegative = false;
				}
			});

			series.points.forEach((point) => {
				if (!point.value) {
					return;
				}

				const pointData: DChartInterface.ChartDataPoint = {
					name: point.title,
					colorIndex: point.colorIndexOrClass,
					y: this.getValueToRender(point.value, hasNegative, isAllNegative),
					dbxPoint: point,
				};
				seriesData.data.push(pointData);
			});

			// "Other" always at the end
			const otherPointValue = this.visualization.otherPointValue;
			if (otherPointValue) {
				const firstPoint = series.points[0];
				const dbxPoint = new DChartPrimaryPoint({
					index: series.points.length,
					value: otherPointValue,
					format: firstPoint?.format,
					scale: firstPoint?.scale,
					granulationLabel: firstPoint?.granulationLabel,
					dateRange: firstPoint?.dateRange,
					title: 'Other',
				});

				const colorIndexOrder = this.dChart.themeData.visualizationConfig.order || [],
					indexesUsed: number[] = [];

				series.points.forEach((p) => {
					if (typeof p.colorIndexOrClass === 'number') {
						indexesUsed.push(p.colorIndexOrClass);
					}
				});
				const colorsLeft = Array.from(colorIndexOrder).filter(
					(c) => !indexesUsed.includes(c),
				);
				if (colorsLeft.length) {
					dbxPoint.colorIndexOrClass = colorsLeft[0]!;
				}

				seriesData.data.push({
					name: dbxPoint.title,
					colorIndex: dbxPoint.colorIndexOrClass,
					y: this.getValueToRender(otherPointValue, hasNegative, isAllNegative),
					dbxPoint,
				});
			}

			seriesDefaultClass = series.points[0]!.onClick ? 'series-clickable' : '';
		}

		seriesData.className =
			seriesDefaultClass + (seriesData.data.length > 1 ? '' : ' one-point');

		options.series.push(seriesData);

		return options;
	}

	override get sizeOptions(): Highcharts.Options {
		let options = super.sizeOptions;

		const m = this.dChart.sizeModifier;

		const blockSizeSmallerSideMin = Math.floor(
				Math.min(this.dChart.maxHeight, this.dChart.maxWidth),
			),
			maxPieSize = blockSizeSmallerSideMin - 20,
			preferredPieSize = Math.max(80, blockSizeSmallerSideMin / 2),
			minPieSize = Math.min(preferredPieSize, maxPieSize),
			spacing = m < 5 ? 0 : Math.min(Math.max(m * 2, 10), 20); // we need some spacing + default margin for longer labels to not be cut off (e.g. https://app.asana.com/0/1180537054235286/1199990418353541/f) and to leave room for the compare-arc drawn outside the pie on hover

		options = this.mergeOptions(options, {
			chart: {
				spacing: [spacing, spacing, spacing, spacing],
			},
			legend: this.defaultLegendOptions,
			plotOptions: {
				pie: {
					minSize: minPieSize,
					dataLabels: {
						padding: m < 8 ? 0 : 3,
						connectorPadding: m < 8 ? 1 : 5,
						distance: m < 5 ? 3 : m < 8 ? 5 : m < 11 ? 15 : 20,
					},
					states: {
						hover: {
							halo: null,
						},
					},
				},
			},
		});

		return options;
	}

	override updateSize(_hasModifierChanged: boolean): void {
		// update also when modifier is unchanged, only size changed bcos od defaultLegendOptions
		super.updateSize(true);
	}

	override updateTheme(): void {
		this.visualization.updateColorIndexes();
		super.updateTheme();
	}

	override formatSeriesTextForLegend(chartPoint: Highcharts.Series | Highcharts.Point): string {
		const point = (chartPoint as DChartInterface.ChartHoverPoint).dbxPoint; // pie legends list the points
		return point.title || `Slice ${chartPoint.index}`;
	}

	private getValueToRender(
		actualValue: number,
		hasNegative: boolean,
		isAllNegative: boolean,
	): number {
		// if all values are negative, take absolute value and show normal pie
		// if just some are negative, ignore them (show as 0)
		let value = 0;
		if (actualValue) {
			if (isAllNegative) {
				value = Math.abs(actualValue);
			} else if (hasNegative) {
				value = Math.max(0, actualValue);
			} else {
				value = actualValue;
			}
		}
		return value;
	}

	private updateDynamicElementsOnRender(hChart: Highcharts.Chart) {
		const mainSeries = hChart.series[0] as PieSeriesInternals | undefined;

		if (!mainSeries) {
			return;
		}

		let centerX = 0,
			centerY = 0,
			innerR = 0,
			sliceThickness = 0;

		const point = mainSeries.points[0];

		if (point) {
			// this is internal API so subject to change

			centerX = point.shapeArgs.x + hChart.plotLeft;
			centerY = point.shapeArgs.y + hChart.plotTop;
			innerR = point.shapeArgs.innerR;
			sliceThickness = point.shapeArgs.r - point.shapeArgs.innerR;
		} else {
			// this way sometimes has slightly offset coordinates
			// https://github.com/highcharts/highcharts/issues/15240

			const mainCenter = mainSeries.center;

			centerX = mainCenter[0] + hChart.plotLeft;
			centerY = mainCenter[1] + hChart.plotTop;
			innerR = mainCenter[3] / 2;
			sliceThickness = (mainCenter[2] - mainCenter[3]) / 2;
		}

		// ------ DRAWING CENTER POINT (testing only) ------
		// if (this.pieCenter) {
		// 	this.pieCenter.attr({
		// 		x: centerX,
		// 		y: centerY
		// 	});
		// } else {
		// 	this.pieCenter = hChart.renderer.circle({
		// 		x: centerX,
		// 		y: centerY,
		// 		r: 3,
		// 		fill: 'red',
		// 	});
		// 	this.pieCenter.add();
		// }

		// ------ DRAWING ARC ------

		const shadowR = innerR + Math.round(sliceThickness / 6), // one sixth width
			shadowInnerR = innerR - 1; // to cover main fully (anti-aliasing)

		if (this.shadowArc) {
			this.shadowArc.animate({
				x: centerX,
				y: centerY,
				r: shadowR,
				innerR: shadowInnerR,
			});
			this.shadowArc.show();
		} else {
			const arc = hChart.renderer.arc(
				centerX,
				centerY,
				shadowR,
				shadowInnerR,
				0,
				2 * Math.PI,
			);
			arc.addClass('shadow-arc').add().toFront();
			this.shadowArc = arc;
		}

		// ------ DRAWING CENTER (AGGREGATION) ------
		const aggregationPoint = this.visualization.aggregationPoint;

		if (!this.isEmpty && typeof aggregationPoint?.value === 'number') {
			const valueStr = formatNumber(aggregationPoint.value, point?.dbxPoint.format, {
					scale: point?.dbxPoint.scale,
				}),
				label = aggregationPoint.label,
				animate =
					this.aggregationLabel &&
					(this.aggregationLabel as HtmlLabel).visibility !== 'hidden';

			if (this.aggregationLabel) {
				const wrapperNode = (this.aggregationLabel as HtmlLabel).div;
				wrapperNode.querySelector('.val')!.textContent = valueStr;
				wrapperNode.querySelector('.label')!.textContent = label;
			} else {
				const newNode = hChart.renderer.label(
					`<span class="val">${valueStr}</span><br><span class="label">${label}</div>`,
					centerX,
					centerY,
					'rect',
					undefined,
					undefined,
					true,
				);
				newNode.addClass('agg-label').add().toFront();
				this.aggregationLabel = newNode;
			}

			// scaling of the aggregation label
			const spaceAvailable = innerR * 2 - 10,
				innerDiv = (this.aggregationLabel as HtmlLabel).div,
				innerSpan = innerDiv.children[0] as HTMLElement;

			innerDiv.style.width = `${spaceAvailable}px`;
			innerDiv.style.height = `${spaceAvailable}px`;
			innerSpan.style.whiteSpace = 'normal';
			innerSpan.style.wordBreak = 'break-all';

			scaleValToAutofit(innerSpan, 0.5, 1.5);

			innerDiv.style.width = 'auto';
			innerDiv.style.height = 'auto';
			innerSpan.style.whiteSpace = 'nowrap';
			innerSpan.style.wordBreak = 'normal';

			// centering
			const labelSize = innerSpan.getBoundingClientRect();

			const newX = centerX - labelSize.width / 2,
				newY = centerY - labelSize.height / 2;

			if (animate) {
				this.aggregationLabel.animate({
					x: newX,
					y: newY,
				});
			} else {
				this.aggregationLabel
					.attr({
						x: newX,
						y: newY,
					})
					.show();
			}
		} else if (this.aggregationLabel) {
			this.aggregationLabel.hide();
		}
	}
}
